import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress, Button } from '@mui/material';
import { liveKitService } from '../services/api';
import LiveKitRoomView from '../components/LiveKitRoomView';

// Dedicated full-page virtual office — what "Open in new tab" on the Attendance-page widget
// (LiveKitPresence) opens, for a specific project's room (?projectId=). Same app/domain, so the
// existing JWT in localStorage carries over automatically; no join token is ever passed through
// the URL. Runs its own project-assignment + attendance-gated access check independently (never
// trusts anything the opening tab might have known).
const reasonText = (reason?: string) => {
  switch (reason) {
    case 'AttendanceRequired': return 'Mark your attendance on the Attendance page to unlock the virtual office.';
    case 'EmployeeInactive': return 'Virtual office access is unavailable for this account.';
    case 'LiveKitDisabled': return 'The virtual office is not enabled right now.';
    case 'NotAssignedToProject': return 'You are not assigned to this project.';
    default: return 'Virtual office access is currently unavailable.';
  }
};

const VirtualOfficePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const projectId = Number(searchParams.get('projectId'));

  const [session, setSession] = useState<{ token: string; serverUrl: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Set when the user deliberately clicks Leave, so the render below can show a "you left" state
  // with a manual rejoin instead of silently reconnecting them (see handleDisconnected).
  const [left, setLeft] = useState(false);

  const connect = useCallback(async () => {
    if (!projectId) {
      setError('No project specified.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setLeft(false);
    try {
      const access = await liveKitService.getTodayAccess(projectId);
      if (access.canJoin && access.token && access.serverUrl) {
        setSession({ token: access.token, serverUrl: access.serverUrl });
      } else {
        setError(reasonText(access.reason));
      }
    } catch {
      setError('Could not verify virtual office access. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    connect();
  }, [connect]);

  // voluntary=true means the user clicked Leave — respect that and show a rejoin screen instead
  // of reconnecting them straight back into the meeting. voluntary=false/undefined is an
  // involuntary drop (network loss, admin force-disconnect, room closed): re-check and reconnect
  // rather than leaving a dead tab.
  const handleDisconnected = (voluntary?: boolean) => {
    setSession(null);
    if (voluntary) {
      setLeft(true);
    } else {
      connect();
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', bgcolor: 'black' }}>
      {loading && (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress sx={{ color: 'white' }} />
        </Box>
      )}
      {!loading && error && (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Alert severity="warning" sx={{ maxWidth: 480 }}>
            <Typography variant="body1">{error}</Typography>
          </Alert>
        </Box>
      )}
      {!loading && !error && !session && left && (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 3 }}>
          <Typography variant="body1" sx={{ color: 'white' }}>You left the virtual office.</Typography>
          <Button variant="contained" onClick={connect}>Rejoin</Button>
        </Box>
      )}
      {!loading && session && (
        <LiveKitRoomView
          token={session.token}
          serverUrl={session.serverUrl}
          projectId={projectId}
          onDisconnected={handleDisconnected}
          fullPage
        />
      )}
    </Box>
  );
};

export default VirtualOfficePage;
